/**
 * Algolia Enhanced Analytics
 * Extends basic Algolia analytics with additional user behavior tracking:
 * - Quick View opens
 * - Variant selections
 * - Filter applications
 * - Direct PDP views (non-search traffic)
 * - Wishlist additions
 *
 * Configuration is read from window.algoliaConfig.enhancedAnalytics
 */

/* global WishlistKing */

class AlgoliaEnhancedAnalytics {
  constructor() {
    this.initialized = false;
    this.config = null;
    this.lastTrackedVariant = null;

    // Start initialization
    this.init();
  }

  /**
   * Initialize when Algolia Insights SDK is ready
   */
  init() {
    const self = this;
    let attempts = 0;
    const maxAttempts = 50;
    const retryInterval = 100;

    function tryInit() {
      attempts++;

      // Check prerequisites
      if (!window.algoliaConfig || !window.algoliaConfig.enabled) {
        self.log('Algolia not enabled, skipping enhanced analytics');
        return;
      }

      if (!window.algoliaConfig.enhancedAnalytics?.enabled) {
        self.log('Enhanced analytics not enabled');
        return;
      }

      // Check for AlgoliaAnalytics.default (CDN loaded) or aa function
      const insightsAvailable = (
        (typeof window.AlgoliaAnalytics !== 'undefined' && window.AlgoliaAnalytics.default) ||
        typeof aa === 'function'
      );

      if (!insightsAvailable) {
        if (attempts < maxAttempts) {
          setTimeout(tryInit, retryInterval);
        } else {
          self.log('Algolia Insights SDK not found after ' + maxAttempts + ' attempts');
        }
        return;
      }

      // SDK is ready
      self.config = window.algoliaConfig;
      self.enhancedConfig = self.config.enhancedAnalytics;
      self.pageContext = self.config.pageContext || {};

      self.setupTracking();
      self.initialized = true;
      self.log('Enhanced analytics initialized');
    }

    // Start after DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', tryInit);
    } else {
      tryInit();
    }
  }

  /**
   * Set up all tracking listeners
   */
  setupTracking() {
    // Quick View tracking
    if (this.enhancedConfig.trackQuickView) {
      this.setupQuickViewTracking();
    }

    // Variant selection tracking
    if (this.enhancedConfig.trackVariants) {
      this.setupVariantTracking();
    }

    // Filter tracking handled by algolia-collection-page.js (not here)

    // Direct PDP view tracking
    if (this.enhancedConfig.trackPdpViews) {
      this.setupPdpViewTracking();
    }

    // Wishlist tracking
    if (this.enhancedConfig.trackWishlist) {
      this.setupWishlistTracking();
    }

    this.log('All tracking listeners set up');
  }

  /**
   * Track Quick View modal opens
   */
  setupQuickViewTracking() {
    const self = this;

    document.addEventListener('quick-view:open', function(event) {
      // Get variant ID from event detail or URL
      const variantId = event.detail?.variantId || event.detail?.variant_id;
      const productUrl = event.detail?.productUrl;

      // Try to extract variant from URL if not in detail
      let objectID = variantId ? String(variantId) : null;

      if (!objectID && productUrl) {
        const urlParams = new URLSearchParams(productUrl.split('?')[1] || '');
        const urlVariant = urlParams.get('variant');
        if (urlVariant) {
          objectID = urlVariant;
        }
      }

      if (objectID) {
        self.trackEvent('viewedObjectIDs', {
          eventName: 'Product Viewed',
          index: self.config.indexName,
          objectIDs: [objectID]
        });

        self.log('Quick View tracked, objectID:', objectID);
      } else {
        self.log('Quick View: no variant ID available');
      }
    });

    this.log('Quick View tracking registered');
  }

  /**
   * Track variant selections on PDP
   */
  setupVariantTracking() {
    const self = this;

    // Check if FoxTheme pubsub is available
    if (typeof FoxTheme === 'undefined' || !FoxTheme.pubsub) {
      this.log('FoxTheme.pubsub not available for variant tracking');
      return;
    }

    const events = FoxTheme.pubsub.PUB_SUB_EVENTS;

    // Standard variant change (V1 selector)
    FoxTheme.pubsub.subscribe(events.variantChange, function(event) {
      if (!event?.data?.variant) return;

      const variant = event.data.variant;
      self.trackVariantSelection(variant);
    });

    // V2 variant selector uses custom DOM event
    document.addEventListener('v2:variantChange', function(event) {
      if (!event.detail?.variant) return;

      const variant = event.detail.variant;
      self.trackVariantSelection(variant);
    });

    this.log('Variant tracking registered');
  }

  /**
   * Track a variant selection with deduplication
   * Uses variant ID as objectID since Algolia index contains variants
   */
  trackVariantSelection(variant) {
    // Deduplicate - don't track same variant twice in a row
    if (this.lastTrackedVariant === variant.id) {
      return;
    }
    this.lastTrackedVariant = variant.id;

    if (!variant.id) {
      this.log('No variant ID available for tracking');
      return;
    }

    // Use variant ID as objectID (matches Algolia index format)
    const objectID = String(variant.id);

    this.trackEvent('clickedObjectIDs', {
      eventName: 'Variant Selected',
      index: this.config.indexName,
      objectIDs: [objectID]
    });

    this.log('Variant tracked:', variant.title || variant.id, 'objectID:', objectID);
  }

  // Filter tracking removed — Algolia-rendered facets bypass FoxTheme.pubsub,
  // so this never fired on collection/search pages. Filter tracking is handled
  // by algolia-collection-page.js trackFilterClick() instead.

  /**
   * Track direct PDP views (visitors not coming from search)
   */
  setupPdpViewTracking() {
    const self = this;

    // Only run on product pages
    if (!this.pageContext.isProduct) {
      return;
    }

    // Wait a bit for Algolia adapter to potentially set queryID
    setTimeout(function() {
      self.trackDirectPdpView();
    }, 1500);

    this.log('PDP view tracking registered');
  }

  /**
   * Track PDP view if not coming from search
   * Uses variant ID as objectID since Algolia index contains variants
   */
  trackDirectPdpView() {
    // Check if there's an active search context (queryID)
    const adapter = window.algoliaCollectionPage || window.algoliaAdapter;
    const hasSearchContext = adapter?.lastQueryID;

    if (hasSearchContext) {
      this.log('PDP has search context, skipping direct view tracking');
      return;
    }

    // Get variant ID from URL or page context
    const variantId = this.getCurrentVariantId();

    if (!variantId) {
      this.log('No variant ID for PDP view tracking');
      return;
    }

    const objectID = String(variantId);

    this.trackEvent('viewedObjectIDs', {
      eventName: 'Product Viewed',
      index: this.config.indexName,
      objectIDs: [objectID]
    });

    this.log('Direct PDP view tracked, objectID:', objectID);
  }

  /**
   * Get current variant ID from URL or page context
   */
  getCurrentVariantId() {
    // First try URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariant = urlParams.get('variant');
    if (urlVariant) {
      return urlVariant;
    }

    // Try page context
    if (this.pageContext.variantId) {
      return this.pageContext.variantId;
    }

    // Try ShopifyAnalytics
    const saVariant = window.ShopifyAnalytics?.meta?.product?.variants?.[0]?.id;
    if (saVariant) {
      return saVariant;
    }

    return null;
  }

  /**
   * Track wishlist additions (WishlistKing integration)
   */
  setupWishlistTracking() {
    const self = this;

    // WishlistKing dispatches custom events
    document.addEventListener('wk:add-to-wishlist', function(event) {
      self.handleWishlistAdd(event.detail);
    });

    // Alternative: WishlistKing toolkit API
    if (typeof WishlistKing !== 'undefined' && WishlistKing.toolkit?.on) {
      try {
        WishlistKing.toolkit.on('add', function(productId) {
          self.handleWishlistAdd({ productId: productId });
        });
      } catch (e) {
        self.log('WishlistKing toolkit binding failed:', e.message);
      }
    }

    this.log('Wishlist tracking registered');
  }

  /**
   * Handle wishlist add event
   * Uses variant ID as objectID since Algolia index contains variants
   */
  handleWishlistAdd(detail) {
    // Prioritize variant ID over product ID/handle
    const variantId = detail?.variantId || detail?.variant_id;
    const productId = detail?.productId || detail?.id;

    // Use variant ID if available, otherwise fall back to product ID
    const objectId = variantId ? String(variantId) : (productId ? String(productId) : null);

    if (!objectId) {
      this.log('No product identifier for wishlist tracking');
      return;
    }

    this.trackEvent('convertedObjectIDs', {
      eventName: 'Wishlist Added',
      index: this.config.indexName,
      objectIDs: [objectId]
    });

    this.log('Wishlist add tracked:', objectId);
  }

  /**
   * Send event to Algolia Insights
   * Skips tracking for bots
   */
  trackEvent(method, payload) {
    // Skip tracking for bots
    if (window.AlgoliaShared?.isBot?.()) {
      return;
    }

    // Use AlgoliaAnalytics.default when available (more reliable)
    const insights = window.AlgoliaAnalytics?.default;

    if (insights && typeof insights[method] === 'function') {
      try {
        insights[method](payload);
        this.log('Event sent via AlgoliaAnalytics:', method, payload.eventName);
      } catch (e) {
        this.log('Error sending event:', e.message);
      }
    } else if (typeof aa === 'function') {
      try {
        aa(method, payload);
        this.log('Event sent via aa():', method, payload.eventName);
      } catch (e) {
        this.log('Error sending event:', e.message);
      }
    } else {
      this.log('Cannot track - no Insights API available');
    }
  }

  /**
   * Extract product handle from URL
   */
  extractProductHandle(url) {
    if (!url) return null;
    const match = url.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  /**
   * Get product handle from current page URL
   */
  getProductHandleFromPage() {
    return this.extractProductHandle(window.location.pathname);
  }

  /**
   * Debug logging
   */
  log(...args) {
    if (this.config?.debug || this.enhancedConfig?.debug) {
      console.warn('[Algolia Enhanced]', ...args);
    }
  }
}

// Initialize when DOM is ready (with guard against double initialization)
document.addEventListener('DOMContentLoaded', function() {
  if (window.algoliaConfig?.enhancedAnalytics?.enabled && !window.algoliaEnhancedAnalytics) {
    window.algoliaEnhancedAnalytics = new AlgoliaEnhancedAnalytics();
  }
});

// Expose class globally for debugging
window.AlgoliaEnhancedAnalytics = AlgoliaEnhancedAnalytics;
